/**
 * V2McpProposalsPage (PRP-MCP-1, reworked in PRP-MCP-5), route
 * `/mcp/proposals[?estimate=<id>]`.
 *
 * Since PRP-MCP-5 chat edits apply to the estimate directly, so nothing links
 * here any more. The route stays for proposals made before that change: per
 * estimate group "Apply all" (approve-all) and "Discard all" (reject each
 * pending one), plus Apply / Discard on each row. Parked ones show the question
 * the chat app must answer, failed ones show why, decided ones sit under a
 * collapsed History. When nothing is open, one line points to the dashboard.
 * Any API error detail (for example a 402 allowance refusal) shows in a Banner.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { McpAPI } from '../../lib/mcpApi';
import type { McpProposal, McpProposalStatus } from '../../lib/mcpApi';
import type { ApiError } from '../../types';
import { Button } from '../components/ui/button';
import { Banner } from '../components/ui/banner';
import { Card } from '../components/ui/card';
import { Pill } from '../components/ui/pill';
import type { PillKind } from '../components/ui/pill';

const STATUS_PILL: Record<McpProposalStatus, PillKind> = {
  pending: 'info',
  parked: 'warn',
  approved: 'ok',
  rejected: 'neutral',
  withdrawn: 'neutral',
  failed: 'bad',
};

const DECIDED: McpProposalStatus[] = ['approved', 'rejected', 'withdrawn'];

/** Formats an ISO timestamp for display, or a dash. */
function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

/** Groups proposals by estimate id, keeping first-seen order. */
function groupByEstimate(proposals: McpProposal[]): [string, McpProposal[]][] {
  const groups = new Map<string, McpProposal[]>();
  for (const p of proposals) {
    const list = groups.get(p.estimate_id) ?? [];
    list.push(p);
    groups.set(p.estimate_id, list);
  }
  return [...groups.entries()];
}

/** One proposal row. Actions are passed only for pending proposals. */
function ProposalRow({
  proposal,
  busy,
  onApprove,
  onReject,
}: {
  proposal: McpProposal;
  busy: boolean;
  onApprove?: () => void;
  onReject?: () => void;
}) {
  return (
    <li
      className="py-3 flex gap-3 items-start"
      data-testid={`proposal-${proposal.proposal_id}`}
    >
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Pill kind={STATUS_PILL[proposal.status] ?? 'neutral'}>{proposal.status}</Pill>
          <span className="text-[11.5px] text-[var(--tx-lo)]">{proposal.kind}</span>
          <span className="text-[11.5px] text-[var(--tx-faint)]">{fmtWhen(proposal.created_at)}</span>
        </div>
        <div className="text-[13px] text-[var(--tx-hi)]">{proposal.summary}</div>
        {proposal.status === 'parked' && (
          <div className="text-[12.5px] text-[var(--tx-mid)] space-y-1">
            {proposal.question && <p className="m-0 font-[550]">{proposal.question}</p>}
            {proposal.options && proposal.options.length > 0 && (
              <ul className="m-0 pl-5 list-disc">
                {proposal.options.map((o) => (
                  <li key={o.choice}>
                    <span className="font-[550]">{o.choice}</span>: {o.description}
                  </li>
                ))}
              </ul>
            )}
            <p className="m-0 text-[var(--tx-lo)]">Answer it under Open items on the estimate page.</p>
          </div>
        )}
        {proposal.status === 'failed' && proposal.reason && (
          <p className="m-0 text-[12.5px] text-[var(--bad)]">{proposal.reason}</p>
        )}
      </div>
      {onApprove && onReject && (
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={onApprove}
            aria-label={`Apply ${proposal.summary}`}
          >
            Apply
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={onReject}
            aria-label={`Discard ${proposal.summary}`}
          >
            Discard
          </Button>
        </div>
      )}
    </li>
  );
}

/** Proposal review page. */
export default function V2McpProposalsPage() {
  const [searchParams] = useSearchParams();
  const estimateFilter = searchParams.get('estimate') ?? undefined;
  const [proposals, setProposals] = useState<McpProposal[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await McpAPI.listProposals({ estimate_id: estimateFilter });
      setProposals(res.proposals ?? []);
    } catch (err) {
      setError((err as ApiError).message || 'Could not load proposals.');
      setProposals((prev) => prev ?? []);
    }
  }, [estimateFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Runs one action, shows its error detail if any, then refreshes. */
  const act = async (fn: () => Promise<unknown>, okText?: (_res: unknown) => string | null) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      const text = okText?.(res) ?? null;
      if (text) setNotice(text);
    } catch (err) {
      setError((err as ApiError).message || 'That action did not go through.');
    } finally {
      await load();
      setBusy(false);
    }
  };

  const groups = useMemo(() => groupByEstimate(proposals ?? []), [proposals]);
  // Only estimates with something still open are shown; the rest are done.
  const openGroups = useMemo(
    () => groups.filter(([, list]) => list.some((p) => !DECIDED.includes(p.status))),
    [groups],
  );

  /** Rejects every pending proposal in one estimate group, one by one. */
  const discardAll = (pending: McpProposal[]) =>
    act(
      async () => {
        let discarded = 0;
        let failed = 0;
        for (const p of pending) {
          try {
            await McpAPI.rejectProposal(p.proposal_id);
            discarded += 1;
          } catch {
            failed += 1;
          }
        }
        return { discarded, failed };
      },
      (r) => {
        const res = r as { discarded: number; failed: number };
        return res.failed > 0
          ? `Discarded ${res.discarded}, could not discard ${res.failed}.`
          : `Discarded ${res.discarded}.`;
      },
    );

  return (
    <div className="max-w-[860px] mx-auto space-y-4">
      <div>
        <h1 className="m-0 text-[19px] font-semibold text-[var(--tx-hi)]">Older chat proposals</h1>
        <p className="mt-1.5 mb-0 text-[13px] text-[var(--tx-mid)]">
          These were made before chat edits applied directly to the estimate. Apply them or
          discard them. New chat edits show on the estimate page, tagged &quot;from chat.&quot;
        </p>
        {estimateFilter && (
          <p className="mt-1.5 mb-0 text-[12px] text-[var(--tx-lo)]">
            Showing one estimate.{' '}
            <Link to={{ search: '' }} className="text-[var(--accent-ink)] underline">
              Show all
            </Link>
          </p>
        )}
      </div>

      {error && <Banner tone="warn">{error}</Banner>}
      {notice && <Banner tone="ok">{notice}</Banner>}

      {proposals === null ? (
        <p className="text-[12.5px] text-[var(--tx-lo)]">Loading...</p>
      ) : openGroups.length === 0 ? (
        <Card className="p-[18px] text-[13px] text-[var(--tx-mid)]" data-testid="proposals-empty">
          Nothing is waiting here. Chat edits now apply to your estimates directly; open them from
          the{' '}
          <Link to="/" className="text-[var(--accent-ink)] underline">
            dashboard
          </Link>
          .
        </Card>
      ) : (
        openGroups.map(([estimateId, list]) => {
          const open = list.filter((p) => !DECIDED.includes(p.status));
          const history = list.filter((p) => DECIDED.includes(p.status));
          const pending = open.filter((p) => p.status === 'pending');
          const pendingCount = pending.length;
          return (
            <Card key={estimateId} className="p-[18px]" data-testid={`estimate-${estimateId}`}>
              <div className="flex items-center gap-3">
                <h2 className="m-0 flex-1 text-[13px] font-semibold text-[var(--tx-hi)]">
                  {/* estimate_id is an import parse hash, not a project id, so no /project link. */}
                  Estimate {estimateId}
                </h2>
                {pendingCount > 0 && (
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => McpAPI.approveAll(estimateId),
                        (r) => {
                          const res = r as { approved?: number; failed?: number };
                          return `Applied ${res.approved ?? 0}, failed ${res.failed ?? 0}.`;
                        },
                      )
                    }
                  >
                    Apply all
                  </Button>
                )}
                {pendingCount > 0 && (
                  <Button size="sm" variant="danger" disabled={busy} onClick={() => void discardAll(pending)}>
                    Discard all
                  </Button>
                )}
              </div>
              {open.length > 0 ? (
                <ul className="m-0 p-0 list-none divide-y divide-[var(--bd-subtle)]">
                  {open.map((p) => (
                    <ProposalRow
                      key={p.proposal_id}
                      proposal={p}
                      busy={busy}
                      onApprove={
                        p.status === 'pending'
                          ? () => void act(() => McpAPI.approveProposal(p.proposal_id))
                          : undefined
                      }
                      onReject={
                        p.status === 'pending'
                          ? () => void act(() => McpAPI.rejectProposal(p.proposal_id))
                          : undefined
                      }
                    />
                  ))}
                </ul>
              ) : (
                <p className="mt-2 mb-0 text-[12.5px] text-[var(--tx-lo)]">Nothing waiting on you.</p>
              )}
              {history.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[12px] text-[var(--tx-mid)]">
                    History ({history.length})
                  </summary>
                  <ul className="m-0 p-0 list-none divide-y divide-[var(--bd-subtle)]">
                    {history.map((p) => (
                      <ProposalRow key={p.proposal_id} proposal={p} busy={busy} />
                    ))}
                  </ul>
                </details>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
}
